import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  Paper,
  Grid,
} from "@material-ui/core";
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Assessment as MetricsIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5, 1),
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(0,0,0,0.06)",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
  },
  label: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  metricsGrid: {
    padding: theme.spacing(1, 0),
  },
  metricCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    textAlign: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.02)",
  },
  metricValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  metricLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  laneRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    fontSize: "0.78rem",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.02)",
    },
  },
  laneColor: {
    width: 10,
    height: 10,
    borderRadius: 3,
    flexShrink: 0,
  },
  laneName: {
    flex: 1,
    fontWeight: 500,
  },
  laneCount: {
    fontWeight: 600,
    minWidth: 24,
    textAlign: "right",
  },
  laneAvg: {
    color: theme.palette.text.secondary,
    fontSize: "0.72rem",
    minWidth: 60,
    textAlign: "right",
  },
  sellerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    fontSize: "0.78rem",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.04)"
          : "rgba(0,0,0,0.02)",
    },
  },
  overloadBadge: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 8,
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "#fff",
  },
  sectionTitle: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}));

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return "0 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

/**
 * DashboardMetrics — header expandível com métricas do board.
 *
 * Calcula client-side a partir dos tickets já carregados:
 *  - Total de tickets no board
 *  - Cards vermelhos (SLA estourado)
 *  - Tempo médio por lane
 *  - Sobrecarga por vendedor (quem tem mais tickets)
 */
const DashboardMetrics = ({ columns, columnTickets, columnCounts }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const metrics = useMemo(() => {
    const now = Date.now();
    let totalTickets = 0;
    let redCards = 0;
    const laneMetrics = [];
    const sellerMap = {};

    for (const col of columns) {
      const tickets = columnTickets[col.id] || [];
      const count = columnCounts[col.id] || tickets.length;
      totalTickets += count;

      const sla = col.settingsJson?.sla || {};
      const redThreshold = (sla.red || 120) * 60000; // ms

      let totalTime = 0;
      let timeCount = 0;
      let colRedCards = 0;

      for (const t of tickets) {
        // SLA check
        if (t.enteredLaneAt) {
          const enteredMs = new Date(t.enteredLaneAt).getTime();
          const elapsed = now - enteredMs;
          if (elapsed >= redThreshold) {
            colRedCards++;
          }
          totalTime += elapsed;
          timeCount++;
        }

        // Seller stats
        const seller = t.user || t.userId;
        const sellerName = t.user?.name || (t.userId ? `ID ${t.userId}` : "Sem atendente");
        const sellerId = t.userId || 0;
        if (!sellerMap[sellerId]) {
          sellerMap[sellerId] = { name: sellerName, count: 0, id: sellerId };
        }
        sellerMap[sellerId].count++;
      }

      redCards += colRedCards;

      laneMetrics.push({
        id: col.id,
        name: col.name,
        color: col.color || "#757575",
        count,
        avgMinutes: timeCount > 0 ? (totalTime / timeCount) / 60000 : 0,
        redCards: colRedCards,
        maxCards: col.settingsJson?.maxCards || 0,
      });
    }

    // Sort sellers by count desc
    const sellers = Object.values(sellerMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Avg across sellers (for overload calculation)
    const avgPerSeller = sellers.length > 0
      ? totalTickets / sellers.filter((s) => s.id !== 0).length || 0
      : 0;

    return { totalTickets, redCards, laneMetrics, sellers, avgPerSeller };
  }, [columns, columnTickets, columnCounts]);

  return (
    <Box className={classes.root}>
      <Box
        className={classes.toggleRow}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <MetricsIcon fontSize="small" color="action" />
        <Typography className={classes.label}>
          Métricas
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginLeft: 4 }}
        >
          {metrics.totalTickets} tickets
          {metrics.redCards > 0 && (
            <span style={{ color: "#F44336", fontWeight: 600, marginLeft: 8 }}>
              {metrics.redCards} em alerta
            </span>
          )}
        </Typography>
        {expanded ? (
          <CollapseIcon fontSize="small" style={{ marginLeft: "auto" }} />
        ) : (
          <ExpandIcon fontSize="small" style={{ marginLeft: "auto" }} />
        )}
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={1} className={classes.metricsGrid}>
          {/* Summary cards */}
          <Grid item xs={6} sm={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Typography className={classes.metricValue}>
                {metrics.totalTickets}
              </Typography>
              <Typography className={classes.metricLabel}>
                Total de Tickets
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Typography
                className={classes.metricValue}
                style={{ color: metrics.redCards > 0 ? "#F44336" : undefined }}
              >
                {metrics.redCards}
              </Typography>
              <Typography className={classes.metricLabel}>
                SLA Estourado
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Typography className={classes.metricValue}>
                {columns.length}
              </Typography>
              <Typography className={classes.metricLabel}>
                Colunas
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Typography className={classes.metricValue}>
                {metrics.sellers.filter((s) => s.id !== 0).length}
              </Typography>
              <Typography className={classes.metricLabel}>
                Vendedores Ativos
              </Typography>
            </Paper>
          </Grid>

          {/* Tempo por Lane */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionTitle}>
              Tempo Médio por Lane
            </Typography>
            {metrics.laneMetrics.map((lane) => (
              <div key={lane.id} className={classes.laneRow}>
                <span
                  className={classes.laneColor}
                  style={{ backgroundColor: lane.color }}
                />
                <span className={classes.laneName}>{lane.name}</span>
                <span className={classes.laneCount}>{lane.count}</span>
                <span className={classes.laneAvg}>
                  {formatDuration(lane.avgMinutes)}
                </span>
                {lane.redCards > 0 && (
                  <span
                    className={classes.overloadBadge}
                    style={{ backgroundColor: "#F44336" }}
                  >
                    {lane.redCards} ⚠
                  </span>
                )}
              </div>
            ))}
          </Grid>

          {/* Sobrecarga por Vendedor */}
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionTitle}>
              Carga por Vendedor
            </Typography>
            {metrics.sellers.length === 0 ? (
              <Typography variant="caption" color="textSecondary">
                Nenhum vendedor atribuído
              </Typography>
            ) : (
              metrics.sellers.map((seller) => {
                const isOverloaded =
                  metrics.avgPerSeller > 0 &&
                  seller.count > metrics.avgPerSeller * 1.5 &&
                  seller.id !== 0;
                return (
                  <div key={seller.id} className={classes.sellerRow}>
                    <span style={{ flex: 1, fontWeight: 500 }}>
                      {seller.name}
                    </span>
                    <span style={{ fontWeight: 600, minWidth: 24, textAlign: "right" }}>
                      {seller.count}
                    </span>
                    {isOverloaded && (
                      <span
                        className={classes.overloadBadge}
                        style={{ backgroundColor: "#FF9800" }}
                      >
                        Alto
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default React.memo(DashboardMetrics);
